import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Search,
  MapPin,
  Calendar,
  Eye,
  Package,
  Car,
  User,
  DollarSign,
  Weight,
  Clock,
  Truck,
  Plane as PlaneIcon,
  Train,
  FileText,
  Mail,
  Phone,
} from 'lucide-react';

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripOwner, setTripOwner] = useState(null);
  const [tripOwners, setTripOwners] = useState({});
  const [tripRequests, setTripRequests] = useState({});
  const [requestUsers, setRequestUsers] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const tripsSnapshot = await getDocs(
        query(collection(db, 'trips'), orderBy('createdAt', 'desc')),
      );
      const tripsData = tripsSnapshot.docs.map(tripDoc => {
        const data = tripDoc.data();

        // Try multiple possible status field names
        let tripStatus =
          data.status ||
          data.tripStatus ||
          data.state ||
          data.currentStatus ||
          null;

        // If status is still null/undefined, try to infer from other fields
        if (!tripStatus) {
          // Check departure date - if it's in the past, might be completed
          if (data.departureDate) {
            const departureDate = data.departureDate?.toDate
              ? data.departureDate.toDate()
              : new Date(data.departureDate);
            const now = new Date();
            // If departure date is more than 7 days ago, likely completed
            if (
              departureDate < now &&
              now - departureDate > 7 * 24 * 60 * 60 * 1000
            ) {
              tripStatus = 'completed';
            } else {
              tripStatus = 'active';
            }
          } else {
            tripStatus = 'active'; // Default fallback
          }
        }

        // Normalize status to lowercase and trim whitespace
        tripStatus = tripStatus?.toString().toLowerCase().trim() || 'active';

        return {
          id: tripDoc.id,
          ...data,
          status: tripStatus, // Use normalized status
        };
      });

      setTrips(tripsData);

      // Fetch all trip owners
      const ownerIds = new Set();
      tripsData.forEach(trip => {
        const ownerId = trip.createdBy || trip.ownerId || trip.userId;
        if (ownerId) {
          ownerIds.add(ownerId);
        }
      });

      // Fetch all owners in parallel
      const ownersMap = {};
      const ownerPromises = Array.from(ownerIds).map(async userId => {
        try {
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            ownersMap[userId] = {
              id: userDoc.id,
              ...userData,
              displayName:
                userData.firstName && userData.lastName
                  ? `${userData.firstName} ${userData.lastName}`
                  : userData.username ||
                    userData.displayName ||
                    userData.email?.split('@')[0] ||
                    'Unknown User',
            };
          }
        } catch (error) {
          console.error(`Error fetching owner ${userId}:`, error);
        }
      });

      await Promise.all(ownerPromises);
      setTripOwners(ownersMap);

      // Update trip statuses based on requests
      // If a trip has completed requests, it might be completed
      const tripStatusMap = {};

      // First, fetch all requests to check trip completion status
      try {
        const allRequestsSnapshot = await getDocs(collection(db, 'requests'));
        allRequestsSnapshot.docs.forEach(reqDoc => {
          const reqData = reqDoc.data();
          const reqStatus = reqData.status?.toLowerCase();
          const tripId = reqData.tripId;

          if (
            tripId &&
            (reqStatus === 'completed' || reqStatus === 'delivered')
          ) {
            // If trip has completed requests, mark trip as potentially completed
            if (!tripStatusMap[tripId]) {
              tripStatusMap[tripId] = 'completed';
            }
          }
        });

        // Update trips with inferred statuses
        tripsData.forEach(trip => {
          // If trip has completed requests and status is missing or active, update to completed
          if (
            tripStatusMap[trip.id] &&
            (!trip.status || trip.status === 'active')
          ) {
            trip.status = tripStatusMap[trip.id];
          }
        });

        setTrips([...tripsData]); // Trigger re-render with updated statuses
      } catch (error) {
        console.error('Error fetching requests for status inference:', error);
      }

      // Fetch requests for completed trips to show requester and traveler info
      const completedTripIds = tripsData
        .filter(trip => {
          const status = trip.status?.toLowerCase();
          return status === 'completed' || status === 'closed';
        })
        .map(trip => trip.id);

      if (completedTripIds.length > 0) {
        const requestsMap = {};
        const requestUserIds = new Set();

        // Fetch all requests for completed trips
        const requestPromises = completedTripIds.map(async tripId => {
          try {
            const requestsSnapshot = await getDocs(
              query(
                collection(db, 'requests'),
                where('tripId', '==', tripId),
                where('status', 'in', ['completed', 'accepted']),
              ),
            );
            const requests = requestsSnapshot.docs.map(reqDoc => ({
              id: reqDoc.id,
              ...reqDoc.data(),
            }));

            if (requests.length > 0) {
              requestsMap[tripId] = requests;
              // Collect user IDs
              requests.forEach(req => {
                if (req.requesterId) requestUserIds.add(req.requesterId);
                if (req.tripOwnerId) requestUserIds.add(req.tripOwnerId);
              });
            }
          } catch (error) {
            console.error(`Error fetching requests for trip ${tripId}:`, error);
          }
        });

        await Promise.all(requestPromises);
        setTripRequests(requestsMap);

        // Fetch all request users (requesters and travelers)
        const usersMap = {};
        const userPromises = Array.from(requestUserIds).map(async userId => {
          try {
            const userDocRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              usersMap[userId] = {
                id: userDoc.id,
                ...userData,
                displayName:
                  userData.firstName && userData.lastName
                    ? `${userData.firstName} ${userData.lastName}`
                    : userData.username ||
                      userData.displayName ||
                      userData.email?.split('@')[0] ||
                      'Unknown User',
              };
            }
          } catch (error) {
            console.error(`Error fetching user ${userId}:`, error);
          }
        });

        await Promise.all(userPromises);
        setRequestUsers(usersMap);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrips = trips.filter(trip => {
    if (!searchTerm && filterStatus === 'all') {
      // No filtering needed
      return true;
    }

    const searchLower = searchTerm.toLowerCase();
    let matchesSearch = false;

    if (searchTerm) {
      // Search in trip basic fields
      const basicFieldsMatch =
        trip.from?.toLowerCase().includes(searchLower) ||
        trip.to?.toLowerCase().includes(searchLower) ||
        trip.originAddress?.toLowerCase().includes(searchLower) ||
        trip.destinationAddress?.toLowerCase().includes(searchLower) ||
        trip.tripType?.toLowerCase().includes(searchLower) ||
        trip.materialType?.toLowerCase().includes(searchLower) ||
        trip.itemDescription?.toLowerCase().includes(searchLower) ||
        trip.description?.toLowerCase().includes(searchLower) ||
        trip.notes?.toLowerCase().includes(searchLower) ||
        trip.deliveryMethod?.toLowerCase().includes(searchLower);

      // Search in origin details
      const originMatch =
        trip.originDetails?.fullAddress?.toLowerCase().includes(searchLower) ||
        trip.originDetails?.city?.toLowerCase().includes(searchLower) ||
        trip.originDetails?.district?.toLowerCase().includes(searchLower) ||
        trip.originDetails?.country?.toLowerCase().includes(searchLower) ||
        trip.originDetails?.street?.toLowerCase().includes(searchLower) ||
        trip.originCity?.toLowerCase().includes(searchLower) ||
        trip.originCountry?.toLowerCase().includes(searchLower);

      // Search in destination details
      const destinationMatch =
        trip.destinationDetails?.fullAddress
          ?.toLowerCase()
          .includes(searchLower) ||
        trip.destinationDetails?.city?.toLowerCase().includes(searchLower) ||
        trip.destinationDetails?.district
          ?.toLowerCase()
          .includes(searchLower) ||
        trip.destinationDetails?.country?.toLowerCase().includes(searchLower) ||
        trip.destinationDetails?.street?.toLowerCase().includes(searchLower) ||
        trip.destinationCity?.toLowerCase().includes(searchLower) ||
        trip.destinationCountry?.toLowerCase().includes(searchLower);

      // Search in trip owner information
      const ownerId = trip.createdBy || trip.ownerId || trip.userId;
      const owner = ownerId ? tripOwners[ownerId] : null;
      const ownerMatch = owner
        ? owner.firstName?.toLowerCase().includes(searchLower) ||
          owner.lastName?.toLowerCase().includes(searchLower) ||
          owner.username?.toLowerCase().includes(searchLower) ||
          owner.email?.toLowerCase().includes(searchLower) ||
          owner.displayName?.toLowerCase().includes(searchLower) ||
          owner.phoneNumber?.toLowerCase().includes(searchLower) ||
          `${owner.firstName || ''} ${owner.lastName || ''}`
            .toLowerCase()
            .trim()
            .includes(searchLower)
        : trip.createdByName?.toLowerCase().includes(searchLower) ||
          trip.ownerName?.toLowerCase().includes(searchLower) ||
          trip.createdByEmail?.toLowerCase().includes(searchLower);

      // Search in price fields
      const priceMatch =
        trip.price?.toString().includes(searchTerm) ||
        trip.flatPrice?.toString().includes(searchTerm) ||
        trip.pricePerUnit?.toString().includes(searchTerm);

      // Search in space/weight fields
      const spaceMatch =
        trip.availableSpace?.toString().includes(searchTerm) ||
        trip.weight?.toString().includes(searchTerm) ||
        trip.spaceUnit?.toLowerCase().includes(searchLower);

      // Search in date fields (format: "Jan 15, 2024")
      let dateMatch = false;
      if (trip.departureDate) {
        try {
          const depDate = trip.departureDate?.toDate
            ? trip.departureDate.toDate()
            : new Date(trip.departureDate);
          const dateStr = depDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          dateMatch = dateStr.toLowerCase().includes(searchLower);
        } catch (e) {
          // Ignore date parsing errors
        }
      }

      matchesSearch =
        basicFieldsMatch ||
        originMatch ||
        destinationMatch ||
        ownerMatch ||
        priceMatch ||
        spaceMatch ||
        dateMatch;
    } else {
      // No search term, match all
      matchesSearch = true;
    }

    // Normalize status for comparison
    const tripStatus = trip.status?.toLowerCase() || 'active';
    const normalizedFilterStatus = filterStatus.toLowerCase();
    const matchesStatus =
      normalizedFilterStatus === 'all' || tripStatus === normalizedFilterStatus;

    return matchesSearch && matchesStatus;
  });

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchTripOwner = async userId => {
    if (!userId) {
      setTripOwner(null);
      return;
    }
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setTripOwner({ id: userDoc.id, ...userDoc.data() });
      } else {
        setTripOwner(null);
      }
    } catch (error) {
      console.error('Error fetching trip owner:', error);
      setTripOwner(null);
    }
  };

  const handleTripSelect = trip => {
    setSelectedTrip(trip);
    const ownerId = trip.createdBy || trip.ownerId || trip.userId;
    if (ownerId) {
      fetchTripOwner(ownerId);
    } else {
      setTripOwner(null);
    }
  };

  const getDeliveryMethodIcon = method => {
    switch (method) {
      case 'byAir':
        return PlaneIcon;
      case 'byRail':
        return Train;
      case 'byRoad':
      default:
        return Truck;
    }
  };

  const getDeliveryMethodLabel = method => {
    switch (method) {
      case 'byAir':
        return 'By Air';
      case 'byRail':
        return 'By Rail';
      case 'byRoad':
      default:
        return 'By Road';
    }
  };

  const getStatusBadge = status => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = status?.toLowerCase() || 'active';
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      completed: { color: 'bg-blue-100 text-blue-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      canceled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' },
      expired: { color: 'bg-orange-100 text-orange-800', label: 'Expired' },
    };
    const config = statusConfig[normalizedStatus] || statusConfig.active;
    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const getTripTypeIcon = tripType => {
    const normalizedType = tripType?.toLowerCase();
    // carrying = Looking for Space (Passenger - wants to carry their own items)
    // shipping = Offering Shipping (Shipping - offering to carry others' items)
    if (
      normalizedType === 'shipping' ||
      normalizedType === 'offering' ||
      normalizedType === 'offeringShipping'
    ) {
      return Package; // Shipping icon
    } else if (
      normalizedType === 'carrying' ||
      normalizedType === 'looking' ||
      normalizedType === 'lookingForSpace' ||
      normalizedType === 'passenger' ||
      normalizedType === 'traveling'
    ) {
      return Car; // Passenger icon
    }
    // Default to Package if unknown
    return Package;
  };

  const getTripTypeLabel = tripType => {
    const normalizedType = tripType?.toLowerCase();
    // carrying = Looking for Space (Passenger)
    // shipping = Offering Shipping (Shipping)
    if (
      normalizedType === 'shipping' ||
      normalizedType === 'offering' ||
      normalizedType === 'offeringShipping'
    ) {
      return 'Shipping';
    } else if (
      normalizedType === 'carrying' ||
      normalizedType === 'looking' ||
      normalizedType === 'lookingForSpace'
    ) {
      return 'Passenger';
    } else if (
      normalizedType === 'passenger' ||
      normalizedType === 'traveling'
    ) {
      return 'Passenger';
    } else if (tripType) {
      return tripType.charAt(0).toUpperCase() + tripType.slice(1);
    }
    return 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trips</h1>
          <p className="text-gray-600 mt-1">Total {trips.length} trips</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search trips (owner name, email, address, route, type, price, etc.)..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none bg-white"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-4 py-3 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none bg-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Trips Table */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-black overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Route
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTrips.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {searchTerm || filterStatus !== 'all'
                      ? 'No trips found'
                      : 'No trips yet'}
                  </td>
                </tr>
              ) : (
                filteredTrips.map(trip => {
                  const TripIcon = getTripTypeIcon(trip.tripType);
                  const ownerId = trip.createdBy || trip.ownerId || trip.userId;
                  const owner = ownerId ? tripOwners[ownerId] : null;
                  const ownerName = owner
                    ? owner.displayName
                    : trip.createdByName || trip.ownerName || 'Unknown User';

                  return (
                    <tr key={trip.id} className="hover:bg-yellow-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="relative h-10 w-10 mr-3 flex-shrink-0">
                            {owner?.photoURL && !imageErrors[ownerId] ? (
                              <img
                                src={owner.photoURL}
                                alt={ownerName}
                                className="h-10 w-10 rounded-full object-cover border-2 border-yellow-500"
                                onError={() => {
                                  // Mark this image as failed
                                  setImageErrors(prev => ({
                                    ...prev,
                                    [ownerId]: true,
                                  }));
                                }}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-yellow-500 flex items-center justify-center border-2 border-black">
                                <span className="text-black font-bold text-sm">
                                  {ownerName
                                    .split(' ')
                                    .filter(n => n)
                                    .map(n => n[0])
                                    .join('')
                                    .toUpperCase()
                                    .slice(0, 2) || 'U'}
                                </span>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {ownerName}
                            </div>
                            {owner?.username && (
                              <div className="text-xs text-gray-500">
                                @{owner.username}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <TripIcon
                            className="text-yellow-600 mr-2"
                            size={20}
                          />
                          <span className="text-sm font-medium text-gray-900">
                            {getTripTypeLabel(trip.tripType)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm">
                          <MapPin size={16} className="text-gray-400 mr-1" />
                          <span className="font-medium text-gray-900">
                            {trip.originAddress ||
                              trip.from ||
                              trip.originDetails?.fullAddress ||
                              trip.originDetails?.city ||
                              'N/A'}
                          </span>
                          <span className="mx-2 text-gray-400">→</span>
                          <span className="font-medium text-gray-900">
                            {trip.destinationAddress ||
                              trip.to ||
                              trip.destinationDetails?.fullAddress ||
                              trip.destinationDetails?.city ||
                              'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar size={16} className="mr-2 text-gray-400" />
                          {formatDate(trip.departureDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(trip.status || 'active')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleTripSelect(trip)}
                          className="text-yellow-600 hover:text-yellow-800 flex items-center"
                        >
                          <Eye size={16} className="mr-1" />
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trip Detail Modal */}
      {selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Trip Details
                </h2>
                <button
                  onClick={() => {
                    setSelectedTrip(null);
                    setTripOwner(null);
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <Package size={16} className="mr-2" />
                      Type
                    </label>
                    <p className="text-gray-900 font-medium">
                      {getTripTypeLabel(selectedTrip.tripType)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <div className="mt-1">
                      {getStatusBadge(selectedTrip.status)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <MapPin size={16} className="mr-2" />
                      Origin
                    </label>
                    <p className="text-gray-900">
                      {selectedTrip.originAddress ||
                        selectedTrip.from ||
                        selectedTrip.originDetails?.fullAddress ||
                        selectedTrip.originDetails?.city ||
                        'N/A'}
                    </p>
                    {selectedTrip.originDetails &&
                      typeof selectedTrip.originDetails === 'object' && (
                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                          {selectedTrip.originDetails.fullAddress && (
                            <p>{selectedTrip.originDetails.fullAddress}</p>
                          )}
                          {(selectedTrip.originDetails.city ||
                            selectedTrip.originDetails.country) && (
                            <p>
                              {[
                                selectedTrip.originDetails.city,
                                selectedTrip.originDetails.district,
                                selectedTrip.originDetails.country,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                          )}
                          {selectedTrip.originDetails.street && (
                            <p>
                              {selectedTrip.originDetails.street}
                              {selectedTrip.originDetails.streetNumber &&
                                ` ${selectedTrip.originDetails.streetNumber}`}
                            </p>
                          )}
                        </div>
                      )}
                    {selectedTrip.originDetails &&
                      typeof selectedTrip.originDetails === 'string' && (
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedTrip.originDetails}
                        </p>
                      )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <MapPin size={16} className="mr-2" />
                      Destination
                    </label>
                    <p className="text-gray-900">
                      {selectedTrip.destinationAddress ||
                        selectedTrip.to ||
                        selectedTrip.destinationDetails?.fullAddress ||
                        selectedTrip.destinationDetails?.city ||
                        'N/A'}
                    </p>
                    {selectedTrip.destinationDetails &&
                      typeof selectedTrip.destinationDetails === 'object' && (
                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                          {selectedTrip.destinationDetails.fullAddress && (
                            <p>{selectedTrip.destinationDetails.fullAddress}</p>
                          )}
                          {(selectedTrip.destinationDetails.city ||
                            selectedTrip.destinationDetails.country) && (
                            <p>
                              {[
                                selectedTrip.destinationDetails.city,
                                selectedTrip.destinationDetails.district,
                                selectedTrip.destinationDetails.country,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                          )}
                          {selectedTrip.destinationDetails.street && (
                            <p>
                              {selectedTrip.destinationDetails.street}
                              {selectedTrip.destinationDetails.streetNumber &&
                                ` ${selectedTrip.destinationDetails.streetNumber}`}
                            </p>
                          )}
                        </div>
                      )}
                    {selectedTrip.destinationDetails &&
                      typeof selectedTrip.destinationDetails === 'string' && (
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedTrip.destinationDetails}
                        </p>
                      )}
                  </div>
                </div>
              </div>

              {/* Delivery & Schedule */}
              <div>
                <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                  Delivery & Schedule
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedTrip.deliveryMethod && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        {(() => {
                          const DeliveryIcon = getDeliveryMethodIcon(
                            selectedTrip.deliveryMethod,
                          );
                          return <DeliveryIcon size={16} className="mr-2" />;
                        })()}
                        Delivery Method
                      </label>
                      <p className="text-gray-900">
                        {getDeliveryMethodLabel(selectedTrip.deliveryMethod)}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <Calendar size={16} className="mr-2" />
                      Departure Date
                    </label>
                    <p className="text-gray-900">
                      {formatDate(selectedTrip.departureDate)}
                    </p>
                  </div>
                  {selectedTrip.departureTime && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <Clock size={16} className="mr-2" />
                        Departure Time
                      </label>
                      <p className="text-gray-900">
                        {selectedTrip.departureTime}
                      </p>
                    </div>
                  )}
                  {selectedTrip.materialType && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Material Type
                      </label>
                      <p className="text-gray-900">
                        {selectedTrip.materialType}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing & Capacity */}
              <div>
                <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                  Pricing & Capacity
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedTrip.pricingMode && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Pricing Mode
                      </label>
                      <p className="text-gray-900 capitalize">
                        {selectedTrip.pricingMode === 'perUnit'
                          ? 'Per Unit'
                          : 'Flat Rate'}
                      </p>
                    </div>
                  )}
                  {selectedTrip.pricePerUnit && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <DollarSign size={16} className="mr-2" />
                        Price Per Unit
                      </label>
                      <p className="text-gray-900">
                        ${selectedTrip.pricePerUnit.toFixed(2)}{' '}
                        {selectedTrip.currency || 'USD'}
                      </p>
                    </div>
                  )}
                  {selectedTrip.flatPrice && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <DollarSign size={16} className="mr-2" />
                        Flat Price
                      </label>
                      <p className="text-gray-900">
                        ${selectedTrip.flatPrice.toFixed(2)}{' '}
                        {selectedTrip.currency || 'USD'}
                      </p>
                    </div>
                  )}
                  {selectedTrip.price && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <DollarSign size={16} className="mr-2" />
                        Price
                      </label>
                      <p className="text-gray-900">
                        ${selectedTrip.price.toFixed(2)}
                      </p>
                    </div>
                  )}
                  {selectedTrip.availableSpace && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <Weight size={16} className="mr-2" />
                        Available Space
                      </label>
                      <p className="text-gray-900">
                        {selectedTrip.availableSpace}{' '}
                        {selectedTrip.spaceUnit || 'kg'}
                      </p>
                    </div>
                  )}
                  {selectedTrip.weight && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <Weight size={16} className="mr-2" />
                        Weight
                      </label>
                      <p className="text-gray-900">{selectedTrip.weight} kg</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description & Notes */}
              {(selectedTrip.itemDescription ||
                selectedTrip.description ||
                selectedTrip.notes) && (
                <div>
                  <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                    Description & Notes
                  </h3>
                  {selectedTrip.itemDescription && (
                    <div className="mb-3">
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <FileText size={16} className="mr-2" />
                        Item Description
                      </label>
                      <p className="text-gray-900 mt-1 bg-gray-50 p-3 rounded">
                        {selectedTrip.itemDescription}
                      </p>
                    </div>
                  )}
                  {selectedTrip.description && (
                    <div className="mb-3">
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <FileText size={16} className="mr-2" />
                        Description
                      </label>
                      <p className="text-gray-900 mt-1 bg-gray-50 p-3 rounded">
                        {selectedTrip.description}
                      </p>
                    </div>
                  )}
                  {selectedTrip.notes && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <FileText size={16} className="mr-2" />
                        Additional Notes
                      </label>
                      <p className="text-gray-900 mt-1 bg-gray-50 p-3 rounded">
                        {selectedTrip.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Trip Owner Information */}
              {tripOwner && (
                <div>
                  <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                    Trip Owner
                  </h3>
                  <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
                    <div className="flex items-center space-x-4">
                      {tripOwner.photoURL ? (
                        <img
                          src={tripOwner.photoURL}
                          alt={tripOwner.firstName || tripOwner.email}
                          className="w-16 h-16 rounded-full"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center">
                          <User className="text-black" size={32} />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-black">
                          {tripOwner.firstName && tripOwner.lastName
                            ? `${tripOwner.firstName} ${tripOwner.lastName}`
                            : tripOwner.username ||
                              tripOwner.email?.split('@')[0] ||
                              'Unknown User'}
                        </h4>
                        {tripOwner.username && (
                          <p className="text-sm text-gray-600">
                            @{tripOwner.username}
                          </p>
                        )}
                        <div className="mt-2 space-y-1">
                          {tripOwner.email && (
                            <div className="flex items-center text-sm text-gray-700">
                              <Mail size={14} className="mr-2" />
                              {tripOwner.email}
                            </div>
                          )}
                          {tripOwner.phoneNumber && (
                            <div className="flex items-center text-sm text-gray-700">
                              <Phone size={14} className="mr-2" />
                              {tripOwner.phoneNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Completed Trip - Requester and Traveler Information */}
              {selectedTrip &&
                (selectedTrip.status?.toLowerCase() === 'completed' ||
                  selectedTrip.status?.toLowerCase() === 'closed') &&
                tripRequests[selectedTrip.id] &&
                tripRequests[selectedTrip.id].length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                      Completed Requests
                    </h3>
                    <div className="space-y-4">
                      {tripRequests[selectedTrip.id].map(request => {
                        const requester = requestUsers[request.requesterId];
                        const traveler =
                          requestUsers[request.tripOwnerId] || tripOwner;

                        return (
                          <div
                            key={request.id}
                            className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200"
                          >
                            <div className="mb-3">
                              <h4 className="font-semibold text-black mb-2">
                                Request: {request.itemTitle || 'Untitled'}
                              </h4>
                              {request.price && (
                                <p className="text-sm text-gray-700">
                                  Price: ${request.price.toFixed(2)}{' '}
                                  {request.currency || 'USD'}
                                </p>
                              )}
                              {request.weight && (
                                <p className="text-sm text-gray-700">
                                  Weight: {request.weight}{' '}
                                  {request.spaceUnit || 'kg'}
                                </p>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Requester (Gönderen) */}
                              {requester && (
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-700 mb-2">
                                    Sender (Requester)
                                  </h5>
                                  <div className="flex items-center space-x-3">
                                    {requester.photoURL ? (
                                      <img
                                        src={requester.photoURL}
                                        alt={requester.displayName}
                                        className="w-12 h-12 rounded-full"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                        <User
                                          className="text-white"
                                          size={20}
                                        />
                                      </div>
                                    )}
                                    <div className="flex-1">
                                      <p className="font-medium text-black">
                                        {requester.displayName}
                                      </p>
                                      {requester.username && (
                                        <p className="text-xs text-gray-600">
                                          @{requester.username}
                                        </p>
                                      )}
                                      {requester.email && (
                                        <p className="text-xs text-gray-600 flex items-center mt-1">
                                          <Mail size={12} className="mr-1" />
                                          {requester.email}
                                        </p>
                                      )}
                                      {requester.phoneNumber && (
                                        <p className="text-xs text-gray-600 flex items-center mt-1">
                                          <Phone size={12} className="mr-1" />
                                          {requester.phoneNumber}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Traveler (Taşıyıcı) */}
                              {traveler && (
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-700 mb-2">
                                    Carrier (Traveler)
                                  </h5>
                                  <div className="flex items-center space-x-3">
                                    {traveler.photoURL ? (
                                      <img
                                        src={traveler.photoURL}
                                        alt={traveler.displayName}
                                        className="w-12 h-12 rounded-full"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                        <User
                                          className="text-white"
                                          size={20}
                                        />
                                      </div>
                                    )}
                                    <div className="flex-1">
                                      <p className="font-medium text-black">
                                        {traveler.firstName && traveler.lastName
                                          ? `${traveler.firstName} ${traveler.lastName}`
                                          : traveler.username ||
                                            traveler.displayName ||
                                            traveler.email?.split('@')[0] ||
                                            'Unknown User'}
                                      </p>
                                      {traveler.username && (
                                        <p className="text-xs text-gray-600">
                                          @{traveler.username}
                                        </p>
                                      )}
                                      {traveler.email && (
                                        <p className="text-xs text-gray-600 flex items-center mt-1">
                                          <Mail size={12} className="mr-1" />
                                          {traveler.email}
                                        </p>
                                      )}
                                      {traveler.phoneNumber && (
                                        <p className="text-xs text-gray-600 flex items-center mt-1">
                                          <Phone size={12} className="mr-1" />
                                          {traveler.phoneNumber}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Metadata */}
              <div>
                <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
                  Metadata
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Created Date
                    </label>
                    <p className="text-gray-900">
                      {formatDateTime(selectedTrip.createdAt)}
                    </p>
                  </div>
                  {selectedTrip.updatedAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Last Updated
                      </label>
                      <p className="text-gray-900">
                        {formatDateTime(selectedTrip.updatedAt)}
                      </p>
                    </div>
                  )}
                  {selectedTrip.contactViaApp !== undefined && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Contact Method
                      </label>
                      <p className="text-gray-900">
                        {selectedTrip.contactViaApp
                          ? 'Via App Only'
                          : 'Direct Contact Allowed'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trips;
